// 医師用診察履歴ページ
import './doctor.scss';
import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../../utils/axios';
import { Calendar, User, Pill, FileText, TestTube, Syringe, ChevronDown, ChevronRight } from 'lucide-react';
import DoctorPageHeader from './DoctorPageHeader';

const DoctorHistoryPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/api/medichat_doctor/post_consult_reviews/history');
        setSessions(data.sessions || []);
      } catch (error) {
        console.error('診察履歴の取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <div className="loader"></div>
      </div>
    );
  }

  const STATUS_MAP = {
    billing_confirmation_pending: { bg: '#fef3c7', color: '#92400e', label: '会計確認中' },
    payment_pending:             { bg: '#dbeafe', color: '#1e40af', label: '支払待ち' },
    delivery_pending:            { bg: '#e9d5ff', color: '#6b21a8', label: '配送準備中' },
    completed:                   { bg: '#d1fae5', color: '#065f46', label: '完了' },
    cancelled:                   { bg: '#f3f4f6', color: '#6b7280', label: 'キャンセル' },
  };

  return (
    <div className="doctor-page-shell doctor-page-shell--wide">
      <DoctorPageHeader title="診療履歴" />

      {sessions.length === 0 ? (
        <div style={{textAlign: 'center', padding: '60px 20px'}}>
          <Calendar size={40} style={{color: '#cbd5e1', margin: '0 auto 12px'}} />
          <p style={{color: '#94a3b8', fontSize: '15px'}}>診療履歴はまだありません</p>
        </div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column'}}>
          {sessions.map((s, idx) => {
            const hasPrescriptions = s.prescriptions && s.prescriptions.length > 0;
            const hasExaminations = s.examinations && s.examinations.length > 0;
            const hasNeedles = s.needles && s.needles.length > 0;
            const hasDocuments = s.documents && s.documents.length > 0;
            const hasDetail = hasPrescriptions || hasExaminations || hasNeedles || hasDocuments;
            const statusInfo = STATUS_MAP[s.status] || { bg: '#f3f4f6', color: '#6b7280', label: s.status };
            const isExpanded = expandedIds.has(s.session_id);
            const isInsurance = s.coverage_kind === 'insurance';
            const isFirst = s.consultation_mode === 'first_visit';

            return (
              <div
                key={s.session_id}
                style={{
                  borderBottom: idx < sessions.length - 1 ? '1px solid #f1f5f9' : 'none',
                  paddingBottom: 0,
                }}
              >
                {/* メイン行 */}
                <div
                  style={{
                    padding: '14px 0',
                    cursor: hasDetail ? 'pointer' : 'default',
                  }}
                  onClick={() => hasDetail && toggleExpand(s.session_id)}
                >
                  {/* 上段: 患者名 + ステータスバッジ */}
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0}}>
                      <User size={15} style={{color: '#94a3b8', flexShrink: 0}} />
                      <span style={{fontSize: '16px', fontWeight: '600', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {s.patient?.name || '患者名不明'}
                      </span>
                    </div>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '22px',
                      padding: '0 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '700',
                      background: statusInfo.bg,
                      color: statusInfo.color,
                      flexShrink: 0,
                      marginLeft: '8px',
                    }}>
                      {statusInfo.label}
                    </span>
                  </div>

                  {/* 下段: 日時 + タグ群 + 展開アイコン */}
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                      <Calendar size={12} style={{color: '#94a3b8'}} />
                      <span style={{fontSize: '13px', color: '#64748b'}}>
                        {new Date(s.consulted_at).toLocaleDateString('ja-JP', {
                          year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short'
                        })}
                      </span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', height: '20px', padding: '0 7px',
                        borderRadius: '5px', fontSize: '11px', fontWeight: '700',
                        background: isInsurance ? '#dcfce7' : '#fff7ed',
                        color: isInsurance ? '#166534' : '#9a3412',
                      }}>
                        {isInsurance ? '保険' : '自費'}
                      </span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', height: '20px', padding: '0 7px',
                        borderRadius: '5px', fontSize: '11px', fontWeight: '700',
                        background: isFirst ? '#dbeafe' : '#f1f5f9',
                        color: isFirst ? '#1e40af' : '#475569',
                      }}>
                        {isFirst ? '初診' : '再診'}
                      </span>
                      {s.primary_disease_name && (
                        <span style={{fontSize: '13px', color: '#475569', fontWeight: '500'}}>
                          {s.primary_disease_name}
                        </span>
                      )}
                    </div>
                    {hasDetail && (
                      isExpanded
                        ? <ChevronDown size={16} style={{color: '#94a3b8', flexShrink: 0}} />
                        : <ChevronRight size={16} style={{color: '#94a3b8', flexShrink: 0}} />
                    )}
                  </div>

                  {/* ID行 */}
                  <div style={{marginTop: '4px', fontSize: '11px', color: '#94a3b8'}}>
                    予約 {s.session_id} · ID {s.patient?.id}
                  </div>
                </div>

                {/* 展開コンテンツ */}
                {isExpanded && hasDetail && (
                  <div style={{padding: '0 0 14px', borderTop: '1px solid #f1f5f9'}}>
                    {hasPrescriptions && (
                      <div style={{paddingTop: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <Pill size={13} style={{color: '#3b82f6'}} />
                          <span style={{fontSize: '12px', fontWeight: '700', color: '#1e40af', letterSpacing: '0.05em'}}>処方薬</span>
                        </div>
                        {s.prescriptions.map((rx, i) => (
                          <div key={i} style={{padding: '6px 0', borderBottom: i < s.prescriptions.length - 1 ? '1px solid #f8fafc' : 'none'}}>
                            <div style={{fontSize: '14px', fontWeight: '600', color: '#0f172a'}}>{rx.drug_name}</div>
                            {rx.count_text && <div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>{rx.count_text}</div>}
                            {rx.instruction_text && <div style={{fontSize: '12px', color: '#64748b'}}>{rx.instruction_text}</div>}
                          </div>
                        ))}
                      </div>
                    )}

                    {hasExaminations && (
                      <div style={{paddingTop: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <TestTube size={13} style={{color: '#f59e0b'}} />
                          <span style={{fontSize: '12px', fontWeight: '700', color: '#92400e', letterSpacing: '0.05em'}}>検査</span>
                        </div>
                        {s.examinations.map((exam, i) => (
                          <div key={i} style={{fontSize: '14px', color: '#0f172a', padding: '4px 0', borderBottom: i < s.examinations.length - 1 ? '1px solid #f8fafc' : 'none'}}>
                            {exam.name}
                          </div>
                        ))}
                      </div>
                    )}

                    {hasNeedles && (
                      <div style={{paddingTop: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <Syringe size={13} style={{color: '#d97706'}} />
                          <span style={{fontSize: '12px', fontWeight: '700', color: '#92400e', letterSpacing: '0.05em'}}>針・器材</span>
                        </div>
                        {s.needles.map((needle, i) => (
                          <div key={i} style={{fontSize: '14px', color: '#0f172a', padding: '4px 0', borderBottom: i < s.needles.length - 1 ? '1px solid #f8fafc' : 'none'}}>
                            {needle.name}
                            {needle.quantity && <span style={{fontSize: '12px', color: '#64748b', marginLeft: '8px'}}>{needle.quantity}{needle.unit || '個'}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {hasDocuments && (
                      <div style={{paddingTop: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                          <FileText size={13} style={{color: '#64748b'}} />
                          <span style={{fontSize: '12px', fontWeight: '700', color: '#475569', letterSpacing: '0.05em'}}>書類</span>
                        </div>
                        {s.documents.map((doc, i) => (
                          <div key={i} style={{fontSize: '14px', color: '#0f172a', padding: '4px 0', borderBottom: i < s.documents.length - 1 ? '1px solid #f8fafc' : 'none'}}>
                            {doc.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DoctorHistoryPage;
